import { ErrorHandler, Injectable, inject } from "@angular/core";
import * as Sentry from "@sentry/browser";
import { AppVersionService } from "../app-version-service/app-version.service";
import { HttpErrorResponse } from "@angular/common/http";
import { AppSettingsService } from "../app-settings-service/app-settings.service";

@Injectable()
export class SentryErrorHandler implements ErrorHandler {
    private appVersionService = inject(AppVersionService);
    private appSettingsService = inject(AppSettingsService);

    private sentryInitialized = false;

    constructor() {}

    public initSentry() {
        if (this.sentryInitialized) return;
        this.sentryInitialized = true;

        this.appVersionService.getVersion().subscribe((data) => {
            let appVersion = data;
            const versionInfo = appVersion.split(".");
            appVersion = versionInfo[0] + "." + versionInfo[1] + "." + versionInfo[2];

            Sentry.init({
                dsn: "https://4e2563789427ac44ee83e3b20c4d4c82@o30831.ingest.us.sentry.io/1521996",
                release: appVersion,
                environment: this.getEnvironmentName(),
                integrations: [Sentry.browserTracingIntegration()],
                tracesSampleRate: this.getTracesSampleRate(),
            });
        });
    }

    getTracesSampleRate() {
        const defaultSampleRate = 0.06;
        try {
            const tracesSampleRate = this.appSettingsService.appSettings?.Sentry_SampleRate;
            if (tracesSampleRate) {
                const json_sampleRate = JSON.parse(tracesSampleRate);
                const env = this.getEnvironmentName();
                if (json_sampleRate && json_sampleRate[env]) {
                    const parsedRate = parseFloat(json_sampleRate[env]);
                    if (!isNaN(parsedRate)) {
                        return parsedRate;
                    }
                }
            }
        } catch (e) {
            console.error("Failed to parse Sentry_SampleRate:", e);
        }
        return defaultSampleRate;
    }

    getEnvironmentName() {
        let environmentName = "localhost";
        const hostname = document.location.host;

        switch (hostname) {
            case "dev.taxprepdashboard.ca":
            case "taxprepdashboard.dev-local.ca:4200":
                environmentName = "Development";
                break;
            case "qc.taxprepdashboard.ca":
                environmentName = "Quality Control";
                break;
            case "qc2.taxprepdashboard.ca":
                environmentName = "Quality Control 2";
                break;
            case "stg.taxprepdashboard.ca":
                environmentName = "Staging";
                break;
            case "www2.taxprepdashboard.ca":
                environmentName = "Production";
                break;
        }

        return environmentName;
    }

    handleError(error) {
        if (error instanceof Promise) {
            error.catch((err) => this.handleError(error));
        } else {
            let extractedError = SentryErrorHandler.extractError(error);
            if (!extractedError) extractedError = "failed to extract error information.";
            const lastEventId = Sentry.captureException(extractedError);

            if (this.getEnvironmentName() === "Development") {
                Sentry.showReportDialog({ eventId: lastEventId });
            }
            throw error;
        }
    }

    public static extractError(error: any): string | Error | null {
        if (!error) return null;

        // Unwrap Angular's zone.js error
        const unwrappedError = this.unwrapAngularError(error);

        // Handle primitive or Error object
        if (typeof unwrappedError === "string" || unwrappedError instanceof Error) {
            return unwrappedError;
        }

        // Handle HttpErrorResponse
        if (unwrappedError instanceof HttpErrorResponse) {
            return this.extractHttpError(unwrappedError);
        }

        // Handle custom error formats
        const customMessage = this.extractCustomErrorMessage(unwrappedError);
        if (customMessage) return customMessage;

        return null;
    }

    private static unwrapAngularError(error: any): any {
        return error?.ngOriginalError ?? error;
    }

    private static extractHttpError(error: HttpErrorResponse): string | Error {
        const err = error.error;

        if (err instanceof Error && err.message) {
            return err;
        }

        if (err instanceof ErrorEvent) {
            return err.message;
        }

        if (typeof err === "string") {
            return `Server returned code ${error.status} with body "${err}"`;
        }

        return error.message;
    }

    private static extractCustomErrorMessage(error: any): string | null {
        if (error?.error?.message) {
            return error.error.message;
        }

        if (error?.message) {
            return error.message;
        }

        return null;
    }
}
